import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { isArray, isEmpty } from 'lodash';
import { AppDispatch, RootState } from '../../../store';

import { Button, Checkbox, Col, Form, Input, App, Modal, Row, Select, Space, Spin, Tooltip } from 'antd';
import { Store } from 'antd/es/form/interface';

import { SmsServiceType, UserTypes } from '../../../shared/constants/enums';
import { SaveSmsSettingRequest, SmsSettingModel } from '../models';
import * as actions from '../store/actions';

const { TextArea } = Input;

export type SmsSettingProps = {
  showModal: boolean;
  onCloseModal: () => void;
  resetAndRefresh: () => void;
};

const SmsSetting = ({ showModal, onCloseModal, resetAndRefresh }: SmsSettingProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const didMount = useRef(false);

  const { modal } = App.useApp();

  const [formRef] = Form.useForm<SaveSmsSettingRequest>();

  const [isModified, setIsModified] = useState<boolean>(false);
  const [initiatingDone, setInitiatingDone] = useState<boolean>(false);
  const [newSmsSetting, setNewSmsSetting] = useState<SmsSettingModel>();

  const smsSetting = useSelector((state: RootState) => state.smsSetting.smsSetting);
  const smsSettingLoading = useSelector((state: RootState) => state.smsSetting.smsSettingLoading);
  const saveSmsSettingLoading = useSelector((state: RootState) => state.smsSetting.saveSmsSettingLoading);
  const messageControlParametersLoading = useSelector((state: RootState) => state.smses.messageControlParametersLoading);

  const messageFooterMaxLength = 100;

  useEffect(() => {
    if (!didMount.current) {
      dispatch(actions.getSmsSetting()).then(() => {
        setInitiatingDone(true);
      });

      didMount.current = true;
    }
  }, []);

  useEffect(() => {
    onReset();
    setNewSmsSetting({ ...smsSetting } as SmsSettingModel);
  }, [smsSetting, smsSettingLoading]);

  const onClose = (isModifiedModal: boolean) => {
    if (isModifiedModal) {
      modal.confirm({
        title: 'All changes will be lost. Are you sure you want to close?',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => {
          onReset();
          onCloseModal?.();
        },
      });
    } else {
      onReset();
      resetAndRefresh?.();
      onCloseModal?.();
    }
  };

  const onFinish = () => {
    setInitiatingDone(false);
    formRef
      .validateFields()
      .then((formData) => {
        if (smsSetting?.userType === UserTypes.Agent) {
          formData.allowUsageFromAgency = false;
        }

        dispatch(actions.saveSmsSetting(formData)).then(() => {
          dispatch(actions.getSmsSetting());
          dispatch(actions.getMessageControlParameters()).then((result) => {
            if (isEmpty(result?.errorMessage)) {
              modal.success({
                title: 'Your settings are saved successfully',
                onOk: () => {
                  setInitiatingDone(true);
                  setIsModified(false);
                  onClose(false);
                },
              });
            }
          });
        });
      })
      .catch(() => {
        setInitiatingDone(true);
      });
  };

  const onReset = () => {
    formRef.resetFields();
    setIsModified(false);
  };

  const onFormChange = (changedValues: any, values: SaveSmsSettingRequest) => {
    if (!isModified) {
      setIsModified(true);
    }

    setNewSmsSetting({ ...values } as SmsSettingModel);
  };

  const validateMessageFooter = (value: string | undefined | null, setFieldValue: (value: string | null) => void) => {
    if (value === undefined || value === null || isEmpty(value)) {
      setFieldValue(null);
      return Promise.resolve(null);
    }

    if (value.trim() === '') {
      return Promise.reject('Message footer should not be only white spaces.');
    }

    return Promise.resolve();
  };

  return (
    <Modal title="SMS setting" open={showModal} footer={null} onCancel={() => onClose(isModified)} onClose={() => onClose(isModified)} width={700}>
      <Spin spinning={smsSettingLoading || !initiatingDone}>
        <Form
          form={formRef}
          size="small"
          className="sms-setting-form"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          initialValues={smsSetting as Store}
          onFinish={onFinish}
          onValuesChange={onFormChange}
        >
          <Form.Item hidden name="id">
            <Input type="hidden" />
          </Form.Item>

          <Form.Item label="Service type" name="smsServiceType" rules={[{ type: 'number', required: true, message: 'The service type is required' }]}>
            <Select placeholder="Select a service type" allowClear={false} value={newSmsSetting?.smsServiceType}>
              {isArray(smsSetting?.smsServiceTypeList)
                ? smsSetting?.smsServiceTypeList.map((e) => (
                    <Select.Option
                      key={e.key}
                      value={e.key}
                      label={e.value}
                      disabled={e.key === SmsServiceType.DefaultRingCentral || e.key === SmsServiceType.DedicatedRingCentral}
                    >
                      {e.value}
                    </Select.Option>
                  ))
                : null}
            </Select>
          </Form.Item>

          {newSmsSetting?.smsServiceType === SmsServiceType.PhoneGroup ? (
            <Form.Item label="Phone Group" name="phoneGroupId" rules={[{ type: 'string', required: true, message: 'The Phone Group is required' }]}>
              <Select placeholder="Select a Phone Group" allowClear={false} value={newSmsSetting?.phoneGroupId ?? 1}>
                {isArray(smsSetting?.phoneGroupList)
                  ? smsSetting?.phoneGroupList.map((e) => (
                      <Select.Option key={e.key} value={e.key} label={e.value}>
                        {e.value}
                      </Select.Option>
                    ))
                  : null}
              </Select>
            </Form.Item>
          ) : undefined}

          <Tooltip title="Enter Your Farewell Message or Sign-off.">
            <Form.Item
              label="Message Footer"
              name="messageFooter"
              rules={[
                ({ setFieldValue }) => ({
                  validator: (_, value) => validateMessageFooter(value, (newValue) => setFieldValue('messageFooter', newValue)),
                }),
                { type: 'string', max: messageFooterMaxLength, message: `Message footer should not exceed ${messageFooterMaxLength} characters.` },
              ]}
            >
              <TextArea rows={2} placeholder={`Max length is ${messageFooterMaxLength}`} maxLength={messageFooterMaxLength} />
            </Form.Item>
          </Tooltip>

          {smsSetting?.userType === UserTypes.Agent ? (
            <Tooltip title="This option is available only for agent.">
              <Form.Item name="useAgencySmsFooterIfNull" rules={[{ type: 'boolean' }]} valuePropName="checked">
                <Checkbox>Use the SMS agency footer if the current one is empty</Checkbox>
              </Form.Item>
            </Tooltip>
          ) : (
            <Form.Item hidden name="useAgencySmsFooterIfNull" valuePropName="checked">
              <Checkbox type="hidden" defaultChecked={false}>
                useAgencySmsFooterIfNull
              </Checkbox>
            </Form.Item>
          )}

          {smsSetting?.userType === UserTypes.Agent ? (
            <Form.Item label="SMS Agency footer">
              <TextArea rows={2} value={smsSetting.agencyMessageFooter} disabled />
            </Form.Item>
          ) : undefined}

          {smsSetting?.userType === UserTypes.InsuranceAgency ? (
            <Tooltip title="This option is available only for agency.">
              <Form.Item name="allowUsageFromAgency" rules={[{ type: 'boolean' }]} valuePropName="checked">
                <Checkbox>Allow account to be used from the whole Agency</Checkbox>
              </Form.Item>
            </Tooltip>
          ) : undefined}

          <Tooltip title="You can mute the chat that you are not interested in. If you use a multi-user SMS account, each user can choose whether or not to subscribe to chat message notifications.">
            <Form.Item name="notificationsOnlyForRecipient" rules={[{ type: 'boolean' }]} valuePropName="checked">
              <Checkbox>Use notification subscription</Checkbox>
            </Form.Item>
          </Tooltip>

          <Tooltip title="Will add the name of the agency to the message header before sending the message in the SMS text-box.">
            <Form.Item name="addAgencyName" rules={[{ type: 'boolean' }]} valuePropName="checked">
              <Checkbox>Add the name of the agency to the message header</Checkbox>
            </Form.Item>
          </Tooltip>

          {smsSetting?.userType === UserTypes.Agent ? (
            <Tooltip title="Will add the name of the agent to the message header before sending the message in the SMS text-box.">
              <Form.Item name="addAgentName" rules={[{ type: 'boolean' }]} valuePropName="checked">
                <Checkbox>Add the name of the agent to the message header</Checkbox>
              </Form.Item>
            </Tooltip>
          ) : (
            <Form.Item hidden name="addAgentName" valuePropName="checked">
              <Checkbox />
            </Form.Item>
          )}

          {newSmsSetting?.smsServiceType === SmsServiceType.DefaultRingCentral ? (
            <Tooltip title="The Default SMS service can't get inbound messages. Will add the cell phone of the agency to the message footer before sending the message in the SMS text-box. This will help answer your message.">
              <Form.Item name="addAgencyCellPhone" rules={[{ type: 'boolean' }]} valuePropName="checked">
                <Checkbox>Add the cell phone of the agency to the message footer</Checkbox>
              </Form.Item>
            </Tooltip>
          ) : (
            <Form.Item hidden name="addAgencyCellPhone" valuePropName="checked">
              <Checkbox />
            </Form.Item>
          )}

          {smsSetting?.userType === UserTypes.Agent && newSmsSetting?.smsServiceType === SmsServiceType.DefaultRingCentral ? (
            <Tooltip title="The Default SMS service can't get inbound messages. Will add the cell phone of the agent to the message footer before sending the message in the SMS text-box. This will help answer your message.">
              <Form.Item name="addAgentCellPhone" rules={[{ type: 'boolean' }]} valuePropName="checked">
                <Checkbox>Add the cell phone of the agent to the message footer</Checkbox>
              </Form.Item>
            </Tooltip>
          ) : (
            <Form.Item hidden name="addAgentCellPhone" valuePropName="checked">
              <Checkbox />
            </Form.Item>
          )}

          <Tooltip title="You will receive an email about new unread message.">
            <Form.Item name="sendEmailOnUnreadMessage" rules={[{ type: 'boolean' }]} valuePropName="checked">
              <Checkbox>Send E-Mail on unread new message</Checkbox>
            </Form.Item>
          </Tooltip>

          <Tooltip title="You will receive an email about new message.">
            <Form.Item name="sendEmailOnNewMessage" rules={[{ type: 'boolean' }]} valuePropName="checked">
              <Checkbox>Send E-Mail on new message</Checkbox>
            </Form.Item>
          </Tooltip>

          <Tooltip title="Allows you to manually mark messages as read and unread if you disable this option.">
            <Form.Item name="autoReadOnChatOpen" rules={[{ type: 'boolean' }]} valuePropName="checked">
              <Checkbox>Auto read on chat open</Checkbox>
            </Form.Item>
          </Tooltip>

          <Row>
            <Col span={24} className="modal-form-control-buttons">
              <Space>
                <Button type="primary" htmlType="submit" loading={smsSettingLoading || saveSmsSettingLoading || messageControlParametersLoading}>
                  {!isEmpty(smsSetting?.id) ? 'Update' : 'Save'}
                </Button>

                <Tooltip title="Reset all changes to the unchanged state">
                  <Button htmlType="button" onClick={onReset} loading={smsSettingLoading || saveSmsSettingLoading || messageControlParametersLoading}>
                    Reset
                  </Button>
                </Tooltip>

                <Button onClick={() => onClose(isModified)} loading={smsSettingLoading || saveSmsSettingLoading || messageControlParametersLoading}>
                  Cancel
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};

export default SmsSetting;
